/* AGC Folding Stock 3D — scroll-driven unfold + teardown.
   Classic script (not a module) so no inline-module or consent-blocker flakiness;
   three.js is pulled in via dynamic import() with retries. Config via data-* on the track. */
(function () {
  'use strict';

  // A store speed-optimizer app transpiles theme JS and rewrites import() into require(),
  // which breaks in the browser. Building the importer at runtime hides it from the transpiler.
  var dynImport = null;
  try { dynImport = new Function('u', 'return import(u)'); } catch (e) { /* CSP blocks eval */ }
  function importModule(url) {
    if (dynImport) return dynImport(url);
    // fallback: a blob module script bridges the import back through a window callback
    return new Promise(function (resolve, reject) {
      var cb = '__agcImp' + Math.floor(Math.random() * 1e9);
      window[cb] = { resolve: resolve, reject: reject };
      var code = 'import * as m from "' + url + '"; window["' + cb + '"].resolve(m); delete window["' + cb + '"];';
      var blobUrl = URL.createObjectURL(new Blob([code], { type: 'text/javascript' }));
      var sc = document.createElement('script');
      sc.type = 'module';
      sc.src = blobUrl;
      sc.onerror = function () { if (window[cb]) { window[cb].reject(new Error('blob import failed: ' + url)); delete window[cb]; } };
      document.head.appendChild(sc);
      setTimeout(function () { if (window[cb]) { window[cb].reject(new Error('import timeout: ' + url)); delete window[cb]; } }, 15000);
    });
  }

  function boot(track) {
    if (track.dataset.fold3dBooted) return;
    track.dataset.fold3dBooted = '1';

    var canvas = track.querySelector('[data-fold3d-canvas]');
    var hintEl = track.querySelector('[data-fold3d-hint]');
    var lockupEl = track.querySelector('[data-fold3d-lockup]');
    var introEl = track.querySelector('[data-fold3d-intro]');
    if (!canvas) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var started = false;
    var attempts = 0;

    function start() {
      if (started) return;
      started = true;
      Promise.all([
        importModule(track.dataset.three),
        importModule(track.dataset.loader),
        importModule(track.dataset.env)
      ]).then(function (mods) {
        run(mods[0], mods[1].GLTFLoader, mods[2].RoomEnvironment);
      }).catch(function (err) {
        console.error('[agc-fold3d] import failed (attempt ' + (attempts + 1) + '):', err);
        started = false;
        if (attempts++ < 3) setTimeout(start, 1200 * attempts);
      });
    }

    function run(THREE, GLTFLoader, RoomEnvironment) {
      var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.12;

      var scene = new THREE.Scene();
      var pmrem = new THREE.PMREMGenerator(renderer);
      scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      // rotated so the bright wall panels land in the camera-facing reflection lobe
      scene.environmentRotation.y = Math.PI;
      // IBL carries the look; directionals only shape the key/rim gradient
      var key = new THREE.DirectionalLight(0xffffff, 0.7);
      key.position.set(0.4, 0.7, 0.3); scene.add(key);
      var rim = new THREE.DirectionalLight(0xe8e8e8, 0.3);
      rim.position.set(-0.5, 0.3, -0.6); scene.add(rim);
      // soft front fill so camera-facing anodized faces sheen instead of crushing to black
      var fill = new THREE.DirectionalLight(0xf4f5f7, 0.35);
      fill.position.set(0.25, -0.05, 1); scene.add(fill);

      var camera = new THREE.PerspectiveCamera(30, 2, 0.001, 10);

      // bead-blast micro-grain: CAD mesh has no UVs, so speckle roughness/normal in
      // local geometry space (sticks to parts as they move during teardown)
      function anodize(mat, grain) {
        mat.onBeforeCompile = function (sh) {
          sh.uniforms.uAgcGrain = { value: grain };
          sh.vertexShader = sh.vertexShader
            .replace('#include <common>', '#include <common>\nvarying vec3 vAgcLP;')
            .replace('#include <begin_vertex>', '#include <begin_vertex>\nvAgcLP = position;');
          sh.fragmentShader = sh.fragmentShader
            .replace('#include <common>', '#include <common>\nvarying vec3 vAgcLP;\nuniform float uAgcGrain;\nfloat agcHash(vec3 p){ p = fract(p * 0.3183099 + vec3(0.71, 0.113, 0.419)); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }')
            .replace('#include <roughnessmap_fragment>', '#include <roughnessmap_fragment>\n{ float g = agcHash(floor(vAgcLP * 2600.0)); roughnessFactor = clamp(roughnessFactor + (g - 0.5) * uAgcGrain, 0.05, 1.0); }')
            .replace('#include <normal_fragment_begin>', '#include <normal_fragment_begin>\n{ vec3 lp = floor(vAgcLP * 3100.0); vec3 gn = vec3(agcHash(lp), agcHash(lp + 7.0), agcHash(lp + 13.0)) - 0.5; normal = normalize(normal + gn * uAgcGrain * 0.3); }');
        };
      }

      // black anodize = metal under a thin oxide layer: full metalness, satin
      // roughness, faint clearcoat for the oxide sheen; base color not pure black
      // or env reflections die and it reads as plastic
      var matBody  = new THREE.MeshPhysicalMaterial({ color: 0x222226, metalness: 1.0, roughness: 0.42, envMapIntensity: 1.4, clearcoat: 0.22, clearcoatRoughness: 0.5 });
      var matArm   = new THREE.MeshPhysicalMaterial({ color: 0x242429, metalness: 1.0, roughness: 0.38, envMapIntensity: 1.4, clearcoat: 0.22, clearcoatRoughness: 0.5 });
      var matStock = new THREE.MeshPhysicalMaterial({ color: 0x1e1e22, metalness: 1.0, roughness: 0.5,  envMapIntensity: 1.25, clearcoat: 0.12, clearcoatRoughness: 0.6 });
      var matSteel = new THREE.MeshPhysicalMaterial({ color: 0xb4b6bc, metalness: 1.0, roughness: 0.3,  envMapIntensity: 1.1 });
      anodize(matBody, 0.14); anodize(matArm, 0.14); anodize(matStock, 0.16); anodize(matSteel, 0.08);

      var PIN = { x: -0.00275, z: 0.01115 }; // hinge pin axis (vertical), meters

      var world = new THREE.Group();
      scene.add(world);
      var foldGroup = new THREE.Group();
      foldGroup.position.set(PIN.x, 0, PIN.z);

      // teardown: real disassembly order, strictly single-axis paths (see project memory before changing).
      // Sequencing rules that keep parts from passing through each other:
      //  - the arm (Buttstock) leaves FIRST on -z; the knuckle follows the same lane
      //    only after the arm has fully cleared (t0 0.74 >= arm t1) — never concurrent
      //  - 00_2 is a z-axis pin (bbox 2.4x2.4x15mm): it extracts along +z, not -y
      //  - thumbscrew travels +75mm so its tail (x1.1+75) clears the jaw before the
      //    jaw slides +20mm; jaw final x<=53.5 stays behind screw tail x>=76
      var EXPLODE = [
        { match: function (n, p, v) { return n.indexOf('00_2') === 0 || p.indexOf('00_2') === 0; }, dir: [0, 0, 1], amt: 0.035, t0: 0.00, t1: 0.22 },
        { match: function (n, p, v) { return p.indexOf('0732-4_2') === 0; },      dir: [0, 1, 0],  amt: 0.052, t0: 0.10, t1: 0.40 },
        { match: function (n, p, v) { return n.indexOf('00_1_1') === 0; },        dir: [0, 1, 0],  amt: 0.030, t0: 0.16, t1: 0.44 },
        { match: function (n, p, v) { return n.indexOf('PRT0001') === 0; },       dir: [1, 0, 0],  amt: 0.075, t0: 0.30, t1: 0.62 },
        { match: function (n, p, v) { return n.indexOf('Buttstock') !== -1; },    dir: [0, 0, -1], amt: 0.115, t0: 0.44, t1: 0.74 },
        { match: function (n, p, v) { return n.indexOf('0732-3') === 0; },        dir: [1, 0, 0],  amt: 0.020, t0: 0.50, t1: 0.80 },
        //  - plate screws share the knuckle's window and lane but travel 15mm less,
        //    so they back out of their countersinks along their own +z axis as the
        //    knuckle recedes; must be listed BEFORE the 7-11-0732 catch-all (first
        //    match wins — their parent node name also starts with 7-11-0732)
        { match: function (n, p, v) { return n.indexOf('AGC_csk') === 0; },       dir: [0, 0, -1], amt: 0.030, t0: 0.74, t1: 0.95 },
        { match: function (n, p, v) { return n.indexOf('7-11-0732') === 0 || p.indexOf('7-11-0732') === 0; }, dir: [0, 0, -1], amt: 0.045, t0: 0.74, t1: 0.95 }
      ];

      var parts = [];
      var loaded = false;
      new GLTFLoader().load(track.dataset.glb, function (gltf) {
        var root = gltf.scene;
        var movers = [];
        root.traverse(function (o) {
          if (!o.isMesh) return;
          var n = o.name, p = o.parent ? o.parent.name : '';
          var nv = o.geometry.getAttribute('position').count;
          if (n.indexOf('Buttstock') !== -1) o.material = matStock;
          else if (['0732-4_2', '00_1_1', '00_2', 'PRT0001'].some(function (s) { return n.indexOf(s) === 0 || p.indexOf(s) === 0; })) o.material = matSteel;
          else if (n.indexOf('7-11-0732') === 0 || p.indexOf('7-11-0732') === 0) o.material = matArm;
          else o.material = matBody;
          if (n.indexOf('Buttstock') !== -1 || n.indexOf('00_2') === 0 || p.indexOf('00_2') === 0 ||
              n.indexOf('7-11-0732') === 0 || p.indexOf('7-11-0732') === 0) movers.push(o);
          for (var i = 0; i < EXPLODE.length; i++) {
            if (EXPLODE[i].match(n, p, nv)) { parts.push({ mesh: o, spec: EXPLODE[i] }); break; }
          }
        });
        world.add(root);
        root.add(foldGroup);
        // the render loop may already have set a fold rotation before the model finished loading;
        // attach() bakes the group's current transform into the children, so zero it first
        foldGroup.rotation.y = 0;
        world.updateWorldMatrix(true, true);
        movers.forEach(function (m) { m.updateWorldMatrix(true, false); foldGroup.attach(m); });
        parts.forEach(function (pt) { pt.base = pt.mesh.position.clone(); });
        root.position.set(0.00275, 0.03, -0.01115); // hinge pin to world origin
        loaded = true;
      });

      // camera: live auto-framing — fits the measured world box every frame (stock excluded during teardown)
      var CAM_DIR = new THREE.Vector3(0.62, 0.26, 0.74).normalize();
      var CAM_DIR_TD = new THREE.Vector3(0.55, 0.52, 0.65).normalize();
      var dirNow = new THREE.Vector3();
      var boxAll = new THREE.Box3(), boxMech = new THREE.Box3(), tmpB = new THREE.Box3();
      var _r = new THREE.Vector3(), _u = new THREE.Vector3(), _d = new THREE.Vector3();
      var cAll = new THREE.Vector3(), cMech = new THREE.Vector3();
      var wantT = new THREE.Vector3(), curT = new THREE.Vector3();
      var curDist = 0, camReady = false, introAmt = 0;
      function fitBoxDist(box, center, dir, fovY, fovX) {
        _r.crossVectors(dir, camera.up).normalize();
        _u.crossVectors(_r, dir).normalize();
        var dh = 0, dv = 0, dd = 0;
        for (var i = 0; i < 8; i++) {
          _d.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).sub(center);
          dh = Math.max(dh, Math.abs(_d.dot(_r)));
          dv = Math.max(dv, Math.abs(_d.dot(_u)));
          dd = Math.max(dd, Math.abs(_d.dot(dir)));
        }
        return Math.max(dh / Math.tan(fovX / 2), dv / Math.tan(fovY / 2)) + dd;
      }
      function frame(fold, tear) {
        var fovY = camera.fov * Math.PI / 180;
        var fovX = 2 * Math.atan(Math.tan(fovY / 2) * camera.aspect);
        dirNow.copy(CAM_DIR).lerp(CAM_DIR_TD, tear).normalize();
        if (!loaded) {
          camera.position.copy(dirNow).multiplyScalar(0.5);
          camera.lookAt(0, 0, 0);
          return;
        }
        world.updateWorldMatrix(true, true);
        boxAll.makeEmpty(); boxMech.makeEmpty();
        world.traverse(function (o) {
          if (!o.isMesh) return;
          if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
          tmpB.copy(o.geometry.boundingBox).applyMatrix4(o.matrixWorld);
          boxAll.union(tmpB);
          if (o.name.indexOf('Buttstock') === -1) boxMech.union(tmpB);
        });
        boxAll.getCenter(cAll); boxMech.getCenter(cMech);
        var dAll = fitBoxDist(boxAll, cAll, dirNow, fovY, fovX);
        var dMech = fitBoxDist(boxMech, cMech, dirNow, fovY, fovX);
        wantT.copy(cAll).lerp(cMech, tear);
        var wantDist = (dAll + (dMech - dAll) * tear) * 1.12;
        // while the intro overlay shows, frame smaller and aim higher so the
        // model sits low on screen, clear of the title block; raising the look
        // target by a fraction of the view height moves the model down by that
        // same fraction of the screen
        if (introAmt > 0) {
          wantDist *= 1 + 0.35 * introAmt;
          wantT.y += 0.16 * introAmt * 2 * wantDist * Math.tan(fovY / 2);
        }
        if (!camReady) { curT.copy(wantT); curDist = wantDist; camReady = true; }
        else { curT.lerp(wantT, 0.12); curDist += (wantDist - curDist) * 0.12; }
        camera.position.copy(curT).addScaledVector(dirNow, curDist);
        camera.lookAt(curT);
      }

      var dragging = false, lastX = 0, yawVel = 0, userYaw = 0;
      canvas.addEventListener('pointerdown', function (e) { dragging = true; lastX = e.clientX; canvas.setPointerCapture(e.pointerId); });
      canvas.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - lastX; lastX = e.clientX;
        userYaw += dx * 0.006; yawVel = dx * 0.006;
      });
      canvas.addEventListener('pointerup', function () { dragging = false; });
      canvas.addEventListener('pointercancel', function () { dragging = false; });

      function easeC(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
      function seg(t, a, b) { return Math.max(0, Math.min(1, (t - a) / (b - a))); }

      function scrollProgress() {
        var r = track.getBoundingClientRect();
        var total = r.height - window.innerHeight;
        return total > 0 ? Math.max(0, Math.min(1, -r.top / total)) : 0;
      }

      // scroll timeline: 0-0.42 unfold | locked hold | 0.52-0.92 teardown | rest tail
      function pose(prog) {
        var fold = 1 - easeC(seg(prog, 0, 0.42));
        var tear = seg(prog, 0.52, 0.92);
        foldGroup.rotation.y = fold * Math.PI;
        for (var i = 0; i < parts.length; i++) {
          var pt = parts[i];
          if (!pt.base) continue;
          var k = easeC(seg(tear, pt.spec.t0, pt.spec.t1)) * pt.spec.amt;
          pt.mesh.position.set(pt.base.x + pt.spec.dir[0] * k, pt.base.y + pt.spec.dir[1] * k, pt.base.z + pt.spec.dir[2] * k);
        }

        // scroll hint stays up while there's still timeline left; fades once the
        // teardown finishes (0.92) so the last stretch reads as "you're done"
        if (hintEl) hintEl.style.opacity = prog > 0.9 ? '0' : '1';
        // intro overlay (homepage) fades out as the scrub starts; the corner
        // lockup takes over right after so both never fight for attention
        introAmt = introEl ? Math.max(0, Math.min(1, 1 - prog / 0.08)) : 0;
        if (introEl) introEl.style.opacity = prog > 0.06 ? '0' : '1';
        var lockFrom = introEl ? 0.06 : 0;
        if (lockupEl) lockupEl.style.opacity = (Math.max(0, Math.min(1, (prog - lockFrom) / 0.05)) * 0.92).toFixed(2);
        if (!dragging) { yawVel *= 0.9; userYaw += yawVel; if (Math.abs(yawVel) < 0.00001) yawVel = 0; }
        world.rotation.y = userYaw;

        var w = canvas.clientWidth || 1200, h = canvas.clientHeight || 700;
        if (canvas.width !== Math.floor(w * renderer.getPixelRatio()) || canvas.height !== Math.floor(h * renderer.getPixelRatio())) {
          renderer.setSize(w, h, false);
          camera.aspect = w / h; camera.updateProjectionMatrix();
        }
        frame(fold, easeC(tear));
        renderer.render(scene, camera);
      }

      var progOverride = null;
      function tick() {
        try {
          pose(progOverride !== null ? progOverride : (reduced ? 0.47 : scrollProgress()));
        } catch (e) {
          console.error('[agc-fold3d] frame error:', e);
        }
        requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);

      window.__agcFold = { scene: scene, camera: camera, world: world, renderer: renderer, pose: pose, setProg: function (v) { progOverride = v; } };
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { io.disconnect(); start(); return; }
        }
      }, { rootMargin: '900px 0px' });
      io.observe(track);
      // scroll fallback in case the observer never fires (odd embed/app contexts)
      var onScroll = function () {
        var r = track.getBoundingClientRect();
        if (r.top < window.innerHeight * 2 && r.bottom > -window.innerHeight) {
          window.removeEventListener('scroll', onScroll);
          start();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    } else {
      start();
    }
  }

  function scan() {
    var tracks = document.querySelectorAll('[data-fold3d]');
    for (var i = 0; i < tracks.length; i++) boot(tracks[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  // late safety net: sections injected or re-rendered after load
  setTimeout(scan, 2500);
})();
